import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import store from '@Store';
import InnerHTML from '@Components/InnerHTML';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ToggleButton from '@Components/Results/reactions/reaction-details/ToggleButton';
import ReactionCIStageDetailsComponent from '@Components/Results/reactions/reaction-details/additional-data/reaction-ci-stage-details.component';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import ReactionStageDetailComponent from '@Components/Results/reactions/reaction-details/reaction-stage-detail.component';
import ReactReactionAdditionalInfoHelper from '@Components/Results/services/react-reaction-additional-info-helper.service';
import ConditionsReferenceExperimentComponent from '@Components/Results/reactions/reaction-details/ConditionsReferenceExperiment';
import ReactionAdditionalInfoComponent from '@Components/Results/reactions/reaction-details/additional-data/reaction-additional-info.component';
import { openMultiStepReactionDialog } from '@Pages/RetroSynthesis/MultiStepReactionDialog/actions';
import { Checkbox } from '@els/els-react--checkbox';
import { CITATION_LINKS_TRACKING_INDEX_MARKER } from '@Components/Results/analytics/citationLinksTracking';

import './ReactionDetailRow.scss';

// eslint-disable-next-line sonarjs/cognitive-complexity
function ReactionDetailRow({
  isHit,
  metadata,
  detail,
  rtProjectId,
  rtRouteId,
  rtParentId,
  rtStepId,
  index,
  largestProduct,
  onFullTextLinkClick = () => null,
  onIssueCitedCounterClick = () => null,
  onAbstractOpen = () => null,
  onAuthorClick = () => null,
  databaseId,
  reactionId,
  renderCheckboxesColumn = false,
  isChecked,
  isVisible = true,
  setChecked,
  allowAdditionalInfoShow = true,
  onExperimentalProcedureOpen = () => null,
  onRenderedRowsNumberChange = () => null,
}) {
  const [experimentalProcedureExpanded, setExperimentalProcedureExpanded] = useState(false);
  const [referencesForcedShow, setReferencesForcedShow] = useState(false);
  const schemeLoadingRef = useRef(false);

  const isCIDatabase = ReactReactionAdditionalInfoHelper.isCIDatabase(databaseId);
  const additionalInfoVisible =
    allowAdditionalInfoShow && isCIDatabase && ReactReactionAdditionalInfoHelper.isAdditionalInfoVisible(detail);

  const recalculateRenderedRowsNumber = useCallback(() => {
    const renderedRows = 1 + (experimentalProcedureExpanded ? 1 : 0) + (referencesForcedShow ? 2 : 0) + (additionalInfoVisible ? 1 : 0);
    onRenderedRowsNumberChange(renderedRows);
  }, [experimentalProcedureExpanded, referencesForcedShow, additionalInfoVisible, onRenderedRowsNumberChange]);

  useEffect(() => {
    recalculateRenderedRowsNumber();
  }, [recalculateRenderedRowsNumber]);

  const getYields = () => {
    const yields = get(detail, 'yields', null);
    return yields
      ? yields.map((yiel, yieldIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="yield-row" key={yieldIndex}>
            {yields.length > 1 && <strong>{ReactReactionDetailsHelper.getLetterTitle(yieldIndex)}&nbsp;</strong>}
            <InnerHTML tag="span" html={(yiel && yiel.yieldValue) || 'n/a'} />
          </div>
        ))
      : null;
  };

  const getMultiStepDetails = () => {
    const multiStepDetails = get(detail, 'multiStepDetails', null);
    return multiStepDetails
      ? multiStepDetails.map((step, stepIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <InnerHTML tag="div" key={stepIndex} className="steps-row" html={step} mode="escape" />
        ))
      : null;
  };

  const getExampleLabelTitle = () => {
    const lbl = detail.exampleLabel;
    const ttl = detail.exampleTitle;
    return `${lbl || ''} ${ttl || ''}`.trim() || 'Experimental Procedure';
  };

  const getTrackingProps = () => {
    const trackingProps = {};
    if (!metadata.oldTrackingDisabled) {
      trackingProps.prepareTrackingData = !isEmpty(detail.citations) ? detail.citations?.[0] : null;
      trackingProps.dataTrackingAction = 'experimental procedure';
    }
    return trackingProps;
  };

  const toggleExperimentalProcedureExpanded = () => {
    setExperimentalProcedureExpanded((prev) => {
      const newValue = !prev;
      if (newValue && !isEmpty(detail.citations)) {
        onExperimentalProcedureOpen(detail.citations?.[0]);
      }
      return newValue;
    });
  };

  const toggleReferencesForcedShow = () => {
    setReferencesForcedShow((prev) => !prev);
  };

  const goToMultipleStepScheme = () => {
    if (!schemeLoadingRef.current) {
      schemeLoadingRef.current = true;
      store.dispatch(
        openMultiStepReactionDialog({
          databaseId: metadata && metadata.databaseId,
          scheme: detail && detail.scheme,
          onClose: () => {
            schemeLoadingRef.current = false;
          },
          rtProjectId,
          rtRouteId,
          rtParentId,
          rtStepId,
        })
      );
    }
  };

  const mainRowClasses = classnames([
    'c-els-table__row',
    experimentalProcedureExpanded ? 'no-border-bottom rx-reactions-table__row-expanded' : null,
    isVisible ? null : 'rx-reactions-table__row-not-visible',
  ]);

  return (
    <>
      <tr className={mainRowClasses} {...(!referencesForcedShow && CITATION_LINKS_TRACKING_INDEX_MARKER.reactionsTableItem)}>
        {/* Conditions section */}
        {renderCheckboxesColumn && (
          <td className="c-els-table__cell c-els-table__basic-cell rx-reactions-table__checkbox">
            <Checkbox
              data-e2e="select-condition"
              id={`checkbox-${reactionId}_${index}`}
              label={`Select ${index}`}
              shouldHideLabel
              checked={isChecked}
              onChange={() => setChecked(detail.id, !isChecked)}
            />
          </td>
        )}
        <td className="c-els-table__cell c-els-table__basic-cell">
          <div className="rx-reactions-table__conditions__steps">
            {detail.multiStepDetails ? (
              <>
                <div className="steps-row">
                  <InnerHTML tag="b" html={detail.classification?.[0]} extendedTags />
                  <b> with </b>
                  <InnerHTML tag="b" html={detail.stepsCount} extendedTags />
                  <b> steps </b>
                </div>
                {getMultiStepDetails()}
                {detail.scheme && (
                  <button type="button" className="els-btn els-btn-xs els-btn-link blue e2e-view-scheme" onClick={goToMultipleStepScheme}>
                    <span>{rtStepId ? 'View/Insert Scheme' : 'View Scheme'}</span>
                    <svg className="els-btn-icon els-btn-icon-tiny">
                      <use xlinkHref="#icon-navigate-right" />
                    </svg>
                  </button>
                )}
              </>
            ) : (
              <>
                {detail.stages &&
                  detail.stages?.map((stage, stageIndex, stages) => (
                    /* eslint-disable react/no-array-index-key */
                    <ReactionStageDetailComponent
                      metadata={metadata}
                      key={stageIndex}
                      index={index}
                      stage={stage}
                      stageIndex={stageIndex}
                      isCIDatabase={isCIDatabase}
                      hasMultipleStages={stages.length > 1}
                    />
                    /* eslint-enable react/no-array-index-key */
                  ))}

                {isCIDatabase && <ReactionCIStageDetailsComponent largestProduct={largestProduct} detail={detail} />}
              </>
            )}
          </div>

          {/* Toggle buttons (Experimental Procedure and Reference) */}
          <div className="u-els-display-flex u-els-margin-top-1o4">
            {detail.experimentalProcedure && (
              <ToggleButton
                className="rx-reactions-table__content-toggle u-els-margin-right-1o2 e2e-experimental-procedure"
                ariaControls={`reaction-procedure-${index}`}
                expanded={experimentalProcedureExpanded}
                toggleExpand={toggleExperimentalProcedureExpanded}
                expandedLabel="Experimental Procedure"
                collapsedLabel="Experimental Procedure"
                isViewPreview={metadata.isViewPreview}
                {...getTrackingProps()}
              />
            )}
            {(!isEmpty(detail.citations) || detail.elnCitation || detail.experiment) && (
              <ToggleButton
                className="rx-reactions-table__content-toggle u-els-display-none u-els-display-table@tablet e2e-show-hide-reference"
                ariaControls={`reaction-references-${index}`}
                expanded={referencesForcedShow}
                toggleExpand={toggleReferencesForcedShow}
                expandedLabel={isCIDatabase ? 'Hide Reference/Experiment' : 'Hide Reference'}
                collapsedLabel={isCIDatabase ? 'Show Reference/Experiment' : 'Show Reference'}
                isViewPreview={metadata.isViewPreview}
              />
            )}
          </div>
        </td>

        {/* Yields section */}
        <td className="c-els-table__cell c-els-table__basic-cell rx-reactions-table__yield">{getYields()}</td>

        {/* Reference/Experiment section - wide screen */}
        <td className="c-els-table__cell c-els-table__basic-cell u-els-display-none@tablet" id={`reaction-references-${index}`}>
          <ConditionsReferenceExperimentComponent
            metadata={metadata}
            index={index}
            detail={detail}
            onFullTextLinkClick={onFullTextLinkClick}
            onIssueCitedCounterClick={onIssueCitedCounterClick}
            onAbstractOpen={onAbstractOpen}
            onAuthorClick={onAuthorClick}
          />
        </td>
      </tr>

      {/* Experimental procedure section */}
      {experimentalProcedureExpanded && (
        <tr className={` c-els-table__row display-table-row ${isVisible ? '' : 'rx-reactions-table__row-not-visible'}`}>
          {renderCheckboxesColumn && <th className="rx-reactions-table__extra" />}
          <td
            className="table__cell c-els-table__cell c-els-table__basic-cell"
            colSpan={renderCheckboxesColumn ? 3 : 4}
            id={`reaction-procedure-${index}`}>
            <div className="rx-reactions-table__header">{getExampleLabelTitle()}</div>
            <InnerHTML tag="div" className="rx-reactions-table__content" html={detail.experimentalProcedure} extendedTags />
          </td>
        </tr>
      )}

      {/* Reference/Experiment section - toggled on small screen */}
      {referencesForcedShow && (
        <>
          <tr className=" c-els-table__row rx-reactions-table__header display-table-row display-table-row-at-tablet">
            <td colSpan="4" className="c-els-table__cell c-els-table__basic-cell rx-reactions-table__references">
              {isCIDatabase ? 'Reference/Experiment' : 'Reference'}
            </td>
          </tr>

          <tr
            className="c-els-table__row rx-reactions-table__item display-table-row-at-tablet"
            {...CITATION_LINKS_TRACKING_INDEX_MARKER.reactionsTableItem}>
            <td className="c-els-table__cell c-els-table__basic-cell" colSpan="4" id={`reaction-references-${index}`}>
              <ConditionsReferenceExperimentComponent
                metadata={metadata}
                index={index}
                detail={detail}
                onFullTextLinkClick={onFullTextLinkClick}
                onIssueCitedCounterClick={onIssueCitedCounterClick}
                onAbstractOpen={onAbstractOpen}
                onAuthorClick={onAuthorClick}
              />
            </td>
          </tr>
        </>
      )}

      {/* Additional CI info section */}
      {additionalInfoVisible && (
        <tr className="c-els-table__row rx-reactions-table__item display-table-row">
          <td className=" c-els-table__cell c-els-table__basic-cell rx-reactions-table__additional-info" colSpan="4">
            <ReactionAdditionalInfoComponent detail={detail} databaseId={databaseId} index={index} reactionId={reactionId} />
          </td>
        </tr>
      )}
    </>
  );
}

ReactionDetailRow.propTypes = {
  metadata: MetadataPropTypesShape,
  detail: PropTypes.object.isRequired,
  index: PropTypes.string.isRequired,
  reactionId: PropTypes.string.isRequired,
  databaseId: PropTypes.string.isRequired,
  isHit: PropTypes.bool,
  spinnerId: PropTypes.string,
  largestProduct: PropTypes.string,
  onExperimentalProcedureOpen: PropTypes.func,
  onFullTextLinkClick: PropTypes.func,
  onIssueCitedCounterClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
  onAuthorClick: PropTypes.func,
  allowAdditionalInfoShow: PropTypes.bool,
  renderCheckboxesColumn: PropTypes.bool,
  isChecked: PropTypes.bool,
  isVisible: PropTypes.bool,
  setChecked: PropTypes.func,
  onRenderedRowsNumberChange: PropTypes.func,
};

export default ReactionDetailRow;
